import { GastoPlan, ModoReparto, PagoPlan } from '../types';

/**
 * Cálculos de A medias. Todo se hace en céntimos enteros para que los repartos
 * cuadren siempre al céntimo y no aparezcan restos como 0,30000000000000004.
 */

export const aCentimos = (euros: number) => Math.round(euros * 100);
export const aEuros = (centimos: number) => centimos / 100;

const sumar = (valores: number[]) => valores.reduce((a, b) => a + b, 0);

/**
 * Reparte un total (céntimos) en proporción a unos pesos, sin perder ni ganar
 * céntimos. Cada uno recibe la parte entera de lo suyo y los céntimos que
 * sobran van, de uno en uno, a quien más fracción perdió; en empate, por el
 * orden de `ids`. Así el resultado es determinista.
 */
export function repartirProporcional(totalCent: number, pesos: Record<string, number>, ids: string[]): Record<string, number> {
  const conPeso = ids.filter(id => (pesos[id] ?? 0) > 0);
  const sumaPesos = sumar(conPeso.map(id => pesos[id]));
  const out: Record<string, number> = {};
  if (conPeso.length === 0 || sumaPesos <= 0) return out;

  const exactos = conPeso.map(id => (totalCent * pesos[id]) / sumaPesos);
  conPeso.forEach((id, i) => { out[id] = Math.floor(exactos[i]); });

  let sobran = totalCent - sumar(Object.values(out));
  const porFraccion = conPeso
    .map((id, i) => ({ id, i, fraccion: exactos[i] - Math.floor(exactos[i]) }))
    .sort((a, b) => b.fraccion - a.fraccion || a.i - b.i);
  for (let k = 0; sobran > 0; k = (k + 1) % porFraccion.length, sobran--) {
    out[porFraccion[k].id] += 1;
  }
  return out;
}

export type ResultadoReparto =
  | { ok: true; importes: Record<string, number> }
  | { ok: false; error: string };

/**
 * Lo que le toca a cada uno de un gasto, en euros.
 * - iguales: `partes` marca con 1 a quien entra en el reparto.
 * - partes:  `partes` son pesos (2 = paga doble).
 * - exacto:  `partes` son los importes en euros y tienen que sumar el total.
 *
 * `orden` fija a quién van primero los céntimos que sobran; conviene que el
 * pagador vaya delante, para que el redondeo no le haga deber a nadie.
 */
export function calcularReparto(
  importe: number, modo: ModoReparto, partes: Record<string, number>, orden: string[],
): ResultadoReparto {
  const totalCent = aCentimos(importe);
  if (totalCent <= 0) return { ok: false, error: 'El importe tiene que ser mayor que cero.' };

  const ids = orden.filter(id => (partes[id] ?? 0) > 0);
  if (ids.length === 0) return { ok: false, error: 'Elige al menos una persona para repartir.' };

  if (modo === 'exacto') {
    const cent: Record<string, number> = {};
    ids.forEach(id => { cent[id] = aCentimos(partes[id]); });
    const suma = sumar(Object.values(cent));
    if (suma !== totalCent) {
      const dif = aEuros(totalCent - suma);
      return {
        ok: false,
        error: dif > 0
          ? `Faltan ${formatoEuros(dif)} por repartir.`
          : `Te has pasado ${formatoEuros(-dif)} del total.`,
      };
    }
    return { ok: true, importes: aEurosMapa(cent) };
  }

  const pesos = modo === 'iguales'
    ? Object.fromEntries(ids.map(id => [id, 1]))
    : partes;
  return { ok: true, importes: aEurosMapa(repartirProporcional(totalCent, pesos, ids)) };
}

const aEurosMapa = (cent: Record<string, number>) =>
  Object.fromEntries(Object.entries(cent).map(([k, v]) => [k, aEuros(v)]));

/**
 * Saldo de cada participante, en euros: lo que pagó menos lo que le tocaba,
 * más los pagos que hizo menos los que recibió. Positivo = le deben;
 * negativo = debe. La suma de todos es siempre cero.
 */
export function calcularSaldos(gastos: GastoPlan[], pagos: PagoPlan[]): Record<string, number> {
  const cent: Record<string, number> = {};
  const mover = (id: string, c: number) => { cent[id] = (cent[id] ?? 0) + c; };

  gastos.forEach(g => {
    Object.entries(g.pagadoPor).forEach(([id, e]) => mover(id, aCentimos(e)));
    Object.entries(g.importes).forEach(([id, e]) => mover(id, -aCentimos(e)));
  });
  pagos.forEach(p => {
    mover(p.de, aCentimos(p.importe));
    mover(p.a, -aCentimos(p.importe));
  });

  return aEurosMapa(cent);
}

export interface Transferencia { de: string; a: string; importe: number }

/**
 * Quién tiene que pagar a quién para dejar a todos a cero, con pocos pagos:
 * el que más debe paga al que más le deben, lo que se pueda, y se repite.
 * Salen como mucho n−1 pagos. No siempre es el mínimo absoluto (eso es un
 * problema NP-difícil), pero en grupos de amigos casi siempre coincide.
 */
export function simplificarDeudas(saldos: Record<string, number>): Transferencia[] {
  const deudores = Object.entries(saldos)
    .map(([id, e]) => ({ id, c: -aCentimos(e) })).filter(x => x.c > 0);
  const acreedores = Object.entries(saldos)
    .map(([id, e]) => ({ id, c: aCentimos(e) })).filter(x => x.c > 0);

  const out: Transferencia[] = [];
  const porMayor = (a: { id: string; c: number }, b: { id: string; c: number }) =>
    b.c - a.c || a.id.localeCompare(b.id);

  while (deudores.length && acreedores.length) {
    deudores.sort(porMayor);
    acreedores.sort(porMayor);
    const d = deudores[0], a = acreedores[0];
    const c = Math.min(d.c, a.c);
    out.push({ de: d.id, a: a.id, importe: aEuros(c) });
    d.c -= c; a.c -= c;
    if (d.c === 0) deudores.shift();
    if (a.c === 0) acreedores.shift();
  }
  return out;
}

export interface ResumenParticipante {
  /** Lo que salió de su bolsillo en gastos. */
  pagado: number;
  /** Lo que le correspondía de los gastos. */
  consumido: number;
  pagosHechos: number;
  pagosRecibidos: number;
  saldo: number;
}

/** Totales por participante para la pestaña Resumen. */
export function resumenPorParticipante(
  gastos: GastoPlan[], pagos: PagoPlan[], ids: string[],
): Record<string, ResumenParticipante> {
  const cent: Record<string, { pagado: number; consumido: number; hechos: number; recibidos: number }> = {};
  const de = (id: string) => (cent[id] ??= { pagado: 0, consumido: 0, hechos: 0, recibidos: 0 });
  ids.forEach(de);

  gastos.forEach(g => {
    Object.entries(g.pagadoPor).forEach(([id, e]) => { de(id).pagado += aCentimos(e); });
    Object.entries(g.importes).forEach(([id, e]) => { de(id).consumido += aCentimos(e); });
  });
  pagos.forEach(p => {
    de(p.de).hechos += aCentimos(p.importe);
    de(p.a).recibidos += aCentimos(p.importe);
  });

  return Object.fromEntries(Object.entries(cent).map(([id, x]) => [id, {
    pagado: aEuros(x.pagado),
    consumido: aEuros(x.consumido),
    pagosHechos: aEuros(x.hechos),
    pagosRecibidos: aEuros(x.recibidos),
    saldo: aEuros(x.pagado - x.consumido + x.hechos - x.recibidos),
  }]));
}

/** Gasto total por categoría, de mayor a menor. Sin categoría va como «Otros». */
export function totalPorCategoria(gastos: GastoPlan[]) {
  const cent = new Map<string, number>();
  gastos.forEach(g => {
    const c = g.categoria?.trim() || 'Otros';
    cent.set(c, (cent.get(c) ?? 0) + aCentimos(g.importe));
  });
  return Array.from(cent.entries())
    .map(([categoria, c]) => ({ categoria, importe: aEuros(c) }))
    .sort((a, b) => b.importe - a.importe);
}

/** ¿Aparece este participante en algún gasto o pago? Si es así no se puede borrar. */
export const participanteConMovimientos = (id: string, gastos: GastoPlan[], pagos: PagoPlan[]) =>
  gastos.some(g => id in g.pagadoPor || id in g.importes) || pagos.some(p => p.de === id || p.a === id);

export const formatoEuros = (e: number) =>
  e.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

/** Saldo por debajo de medio céntimo: a efectos prácticos, cero. */
export const esCero = (e: number) => Math.abs(e) < 0.005;
